import { memo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from'lodash';

// Import Swiper React components
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
// eslint-disable-next-line import/no-unresolved
import { Autoplay, Pagination, Navigation } from 'swiper/modules';

// @mui
import { List, ListItem, Stack, Typography, Box, ButtonBase } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';

//
import { IconOverlay } from '../../../@popover/IconOverlay';
import { TypographyLink } from '../../../../components/hovers';

import { GLOBAL_CATALOGS } from '../../../../store/types';
import { saveRecentItem } from '../../../../utils/@helper/application.helper';
import { SvgArrowNarrowLeftFill, SvgArrowNarrowRightFill } from '../../../../components/SvgIcons';


const SwiperNavServiceContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  '& .swiper': {
    [theme.breakpoints.down('sm')]: {
      marginInline: -12,
      paddingInline: 12,
    },
    '& .swiper-wrapper': {
      '& .swiper-slide': {
        '& .Sim-swipe-item': {
          display: 'block',
          textDecoration: 'auto',
          cursor: 'pointer',
          maxHeight: '100px',
          minHeight: '64px',
          padding: theme.spacing(4, 4.5),
          transition: 'all 0.3s ease-out',
          border: `1px solid ${theme.colors.recommend.borderColor}`,
          backgroundColor: theme.colors.recommend.background,
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2.25, 1),
          },
          '& svg': {
            opacity: 0,
            transition: 'all 0.3s ease-out',
          },
          '&:hover': {
            backgroundColor: theme.palette.color.primaryBlue2,
            borderColor: theme.palette.color.primaryBlue2,
            '& svg': {
              opacity: 1,
            },
          }
        },
      },
    }
  }
}));

const SwiperNavServiceItem = styled(SwiperSlide)(({ theme }) => ({
  paddingInline: theme.spacing(1)
}));


const SwiperNavServiceButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'aninationName',
})(({ aninationName, theme }) => ({
  overflow: 'hidden',
  position: 'absolute',
  top: `calc(50%)`,
  transform: 'translateY(-50%)',
  zIndex: 3,
  backgroundColor: theme.palette.color.secondaryGreen2,
  padding: theme.spacing(1.5),
  transition: '0.3s cubic-bezier(0.5, 1, 0.89, 1)',
  '&.swiper-nav-service-btn-prev': {
    left: '-40px'
  },
  '&.swiper-nav-service-btn-next': {
    right: '-40px'
  },
  '&.swiper-button-disabled': {
    opacity: 0,
    visibility: 'hidden',
  },
  '&:hover': {
    '& svg': {
      animation: `${aninationName} .3s forwards`,
    }
  },
  '&[disabled]': {
    display: 'none',
    'svg': {
      fill: 'rgba(255, 255, 255, 0.32)',
      color: 'rgba(255, 255, 255, 0.32)',
    }
  }
}));

const SwiperNavServicePagination = styled(Box)(({ theme }) => ({
  width: '100%',
  textAlign: 'center',
  position: 'absolute',
  bottom: '80px !important',
  zIndex: 3,
  '& .swiper-pagination-bullet': {
    width: 16,
    height: 4,
    borderRadius: 0,
    marginRight: 4,
    backgroundColor: `rgba(255, 255, 255, 0.6)`,
    transition: '0.3s cubic-bezier(0.5, 1, 0.89, 1)',
  },
  '& .swiper-pagination-bullet-active': {
    width: 32
  }
}));

const HorizontalNavList = ({ theme, list = [] }) => {

  return (
    <SwiperNavServiceContainer position={'relative'}>
      <Swiper
        freeMode
        spaceBetween={16}
        slidesPerView={11}
        slidesPerGroup={11}
        centerInsufficientSlides={Boolean(true)}
        centeredSlides={Boolean(false)}
        initialSlide={0}
        pagination={{
          clickable: true,
          el: '.swiper-nav-service-dot-pagination',
        }}
        navigation={{
          prevEl: '.swiper-nav-service-btn-prev',
          nextEl: '.swiper-nav-service-btn-next'
        }}
        modules={[Pagination, Navigation, Autoplay]}
        breakpoints={{
          1800: {
            slidesPerView: 11,
            slidesPerGroup: 11,
            spaceBetween: 0,
          },
          1600: {
            slidesPerView: 9,
            slidesPerGroup: 9,
            spaceBetween: 16,
          },
          1400: {
            slidesPerView: 6,
            slidesPerGroup: 6,
            spaceBetween: 16,
          },
          1200: {
            slidesPerView: 6,
            slidesPerGroup: 6,
            spaceBetween: 8,
          },
        }}
      >
        {
          _.map(list, (catalogs, catalogsIndex) => {
            return (
              <SwiperNavServiceItem key={catalogsIndex}>
                <Typography 
                variant={`h6`} 
                component={RouterLink} 
                to={catalogs?.props?.path} 
                color="common.black" 
                mb={2} 
                sx={{ textDecoration: 'auto', display: 'block' }}
                noWrap
                onClick={() => saveRecentItem(catalogs)}
                >
                  {catalogs?.label}
                </Typography>
                <List 
                  disablePadding
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {
                    _.map(catalogs?.children, cell => {
                      return (
                        <ListItem 
                          key={cell?.id}
                          disablePadding
                          sx={{
                            cursor: 'pointer',
                            ...theme.typography.hoverPrimaryLeftline1px,
                            '& .Sim-horizontal-nav-link': {
                              // transition: 'all 300ms ease',
                              '& svg': {
                                opacity: 0
                              },
                              '&:hover': {
                                color: theme.palette.action.selected,
                                '& svg': {
                                  opacity: 1
                                }
                              }
                            },
                          }}
                        >
                          <TypographyLink 
                            className={'Sim-horizontal-nav-link'}
                            variant={'body1'}
                            disableBoxShadow 
                            bgColor={'transparent'}
                            textColor={'text.primary'}
                            iconColor={theme.palette.action.selected}
                            sx={{
                              px: 0,
                              py: 0.25,
                              width: 'inherit',
                              display: 'flex',
                              justifyContent: 'space-between',
                              minHeight: 0,
                            }}
                            primary={cell?.label}
                            to={cell?.props?.path}
                            onClick={() => saveRecentItem(catalogs)}
                            data-path={cell?.props?.path}
                            noWrap
                          />
                        </ListItem>
                      )
                    })
                  }
                </List>
              </SwiperNavServiceItem>
            )
          })
        }
      </Swiper>

      <SwiperNavServiceButton aninationName={'toLeftFromRight'} className={'swiper-nav-service-btn-prev'} sx={{ left: 0 }}>
        <SvgArrowNarrowLeftFill color={'#FFFFFF'} />
      </SwiperNavServiceButton>
      <SwiperNavServiceButton aninationName={'toRightFromLeft'} className={'swiper-nav-service-btn-next'} sx={{ right: 0 }}>
        <SvgArrowNarrowRightFill color={'#FFFFFF'} />
      </SwiperNavServiceButton>
    </SwiperNavServiceContainer>
  )

  // return (
  //   <List 
  //     disablePadding
  //     sx={{
  //       display: 'flex',
  //       justifyContent: 'center',
  //     }}
  //   >
  //     {
  //       _.map(list, catalogs => {
  //         return (
  //           <ListItem
  //             key={catalogs?.id}
  //             disablePadding
  //             sx={{
  //               width: 'auto',
  //               display: 'flex',
  //               alignItems: 'flex-start',
  //               flexDirection: 'column',
  //               paddingInline: 2,
  //               // minWidth: 214,
  //             }}
  //           >
  //             <Typography 
  //              variant={`h6`} 
  //              component={RouterLink} 
  //              to={catalogs?.props?.path} 
  //              color="common.black" 
  //              mb={2} 
  //              sx={{ textDecoration: 'auto' }}
  //              onClick={() => saveRecentItem(catalogs)}
  //             >
  //               {catalogs?.label}</Typography>
  //             <List 
  //               disablePadding
  //               sx={{
  //                 display: 'flex',
  //                 alignItems: 'flex-start',
  //                 flexDirection: 'column',
  //                 gap: 2,
  //               }}
  //             >
  //               {
  //                 _.map(catalogs?.children, cell => {
  //                   return (
  //                     <ListItem 
  //                       key={cell?.id}
  //                       disablePadding
  //                       sx={{
  //                         cursor: 'pointer',
  //                         ...theme.typography.hoverPrimaryLeftline1px,
  //                         '& .Sim-horizontal-nav-link': {
  //                           // transition: 'all 300ms ease',
  //                           '& svg': {
  //                             opacity: 0
  //                           },
  //                           '&:hover': {
  //                             color: theme.palette.action.selected,
  //                             '& svg': {
  //                               opacity: 1
  //                             }
  //                           }
  //                         },
  //                       }}
  //                     >
  //                       <TypographyLink 
  //                         className={'Sim-horizontal-nav-link'}
  //                         variant={'body1'}
  //                         disableBoxShadow 
  //                         bgColor={'transparent'}
  //                         textColor={'text.primary'}
  //                         iconColor={theme.palette.action.selected}
  //                         sx={{
  //                           px: 0,
  //                           py: 0.25,
  //                           width: 'inherit',
  //                           display: 'flex',
  //                           justifyContent: 'space-between',
  //                           minHeight: 0,
  //                         }}
  //                         primary={cell?.label}
  //                         to={cell?.props?.path}
  //                         onClick={() => saveRecentItem(catalogs)}
  //                         data-path={cell?.props?.path}
  //                       />
  //                     </ListItem>
  //                   )
  //                 })
  //               }
  //             </List>
  //           </ListItem>
  //         )
  //       })
  //     }
  //   </List>
  // )
}

// ==============================|| Nav Horizontal ||============================== //
NavSectionHorizontal.propTypes = {
  navConfig: PropTypes.array,
};
function NavSectionHorizontal({ navConfig = [] }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  return (
    <Stack direction="row" justifyContent="center" minHeight={'100%'}>
      {_.map(navConfig, (item, index) => (
        <IconOverlay 
          key={index}
          selected={[GLOBAL_CATALOGS.Service.Root].includes(item?.contentCode) && pathname?.includes('/service/')}
          path={(![GLOBAL_CATALOGS.Service.Root].includes(item?.contentCode)) && item?.props?.path}
          primary={<Typography variant={'body1'} height={1} display={'flex'} alignItems={'center'} onClick={() => [GLOBAL_CATALOGS.About.Root, GLOBAL_CATALOGS.Bulletins.Root, GLOBAL_CATALOGS.Faqs.Root, GLOBAL_CATALOGS.Documents.Root].includes(item?.contentCode) && saveRecentItem(item)}>{item?.label}</Typography>}
          content={item?.children && [GLOBAL_CATALOGS.Service.Root].includes(item?.contentCode) && (<HorizontalNavList theme={theme} list={item?.children} />)}
        />
      ))}
    </Stack>
  );
}

export default memo(NavSectionHorizontal);
